import type { MotnStreamingOption } from '@setplex/tria-api'

import {
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { model as i18n } from '~/core/i18n'
import { register } from '~/core/repository'
import { motnOpenProvider } from '~/features/analytix'
import { normalizeMotnStreamingOptions } from './lib/normalizeMotnStreamingOptions'

export type { MotnStreamingOptionRow } from './lib/normalizeMotnStreamingOptions'

export const openMotnStreamingOptionLinkFx = createEffect<
  { link?: string },
  void
>(({ link }) => {
  if (!link) return
  try {
    window.open(link, '_blank', 'noopener,noreferrer')
  } catch {
    // Popup blocked or invalid URL
  }
})

export const openMotnStreamingOptionLink = createEvent<{
  serviceName: string
  accessInfo: string | null
  link?: string
}>()

sample({
  clock: openMotnStreamingOptionLink,
  target: openMotnStreamingOptionLinkFx,
})

export const openMotnStreamingOptionsPopup = createEvent<{
  item: { contentType?: 'MOTN_TV_SHOW' | 'MOTN_VOD'; name?: string }
  options: MotnStreamingOption[]
}>()

export const closeMotnStreamingOptionsPopup = createEvent()

const $motnStreamingOptionsRaw = createStore<MotnStreamingOption[]>([]) //
  .on(openMotnStreamingOptionsPopup, (_, { options }) => options)

export const $motnStreamingOptionsPopupItems = combine(
  i18n.$t,
  $motnStreamingOptionsRaw,
  ({ t }, options) => normalizeMotnStreamingOptions(options, t)
)

export const $motnStreamingOptionsPopupOpened = createStore(false)
  .on(openMotnStreamingOptionsPopup, () => true)
  .on(closeMotnStreamingOptionsPopup, () => false)

sample({
  clock: openMotnStreamingOptionLink,
  source: openMotnStreamingOptionsPopup,
  fn: ({ item }, option) => ({
    contentType: item.contentType,
    name: item.name,
    serviceName: option.serviceName,
    accessInfo: option.accessInfo ?? undefined,
  }),
  target: motnOpenProvider,
})

sample({
  clock: openMotnStreamingOptionLinkFx.finally,
  target: closeMotnStreamingOptionsPopup,
})

register(
  $motnStreamingOptionsPopupOpened,
  '$motnStreamingOptionsPopup/isOpened'
)
register($motnStreamingOptionsPopupItems, '$motnStreamingOptionsPopup/options')
register(
  closeMotnStreamingOptionsPopup,
  'motnStreamingOptionsPopup/closeMotnStreamingOptionsPopup'
)
register(
  openMotnStreamingOptionLink,
  'motnStreamingOptionsPopup/openStreamingOptionLink'
)
